// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/HistoricoMovProduto.tsx
//  Histórico de movimentações por produto — filtros: produto, local, tipo, período
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useEstoqueAnalitico } from '@/features/estoque/hooks/useRelatoriosEstoque';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { TIPOS_MOVIMENTO } from '@/features/estoque/types';

const PAGE_SIZES = [20, 50, 100] as const;

const today   = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};

function tipoVariant(id: number): 'default' | 'destructive' | 'secondary' | 'outline' {
  if (id === 1) return 'default';       // Entrada
  if (id === 2) return 'destructive';   // Saída
  if (id === 3) return 'secondary';     // Transferência
  return 'outline';                     // Ajustes
}

const fmtQtd  = (v: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

// ─────────────────────────────────────────────────────────────────────────────

const HistoricoMovProduto = () => {
  const [produtoId,     setProdutoId]     = useState<number | undefined>(undefined);
  const [localId,       setLocalId]       = useState<string>('');
  const [tipoId,        setTipoId]        = useState<string>('');
  const [dtInicio,      setDtInicio]      = useState(daysAgo(30));
  const [dtFim,         setDtFim]         = useState(today());
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  const { data, isLoading, isFetching } = useEstoqueAnalitico({
    produtoId,
    localId:  localId  ? Number(localId)  : undefined,
    tipoId:   tipoId   ? Number(tipoId)   : undefined,
    dtInicio,
    dtFim,
    pagina,
    tamanhoPagina,
  });

  const { data: locais = [] } = useLocalEstoqueCombo();

  const total        = data?.total        ?? 0;
  const totalPaginas = data?.totalPaginas ?? 1;
  const itens        = data?.itens        ?? [];
  const firstItem    = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem     = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (v: string) => { setTamanhoPagina(Number(v)); setPagina(1); };
  const resetPagina          = ()           => setPagina(1);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Histórico de Movimentações</h1>
        <p className="text-muted-foreground mt-1">
          Todas as movimentações de estoque com saldo anterior e atual
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Produto</Label>
              <ProdutoSearchCombobox
                value={produtoId}
                onChange={(v) => { setProdutoId(v || undefined); resetPagina(); }}
                placeholder="Todos os produtos..."
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Local de Estoque</Label>
              <Select value={localId} onValueChange={(v) => { setLocalId(v === 'todos' ? '' : v); resetPagina(); }}>
                <SelectTrigger><SelectValue placeholder="Todos os locais" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os locais</SelectItem>
                  {locais.map((l) => <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Tipo de Movimento</Label>
              <Select value={tipoId} onValueChange={(v) => { setTipoId(v === 'todos' ? '' : v); resetPagina(); }}>
                <SelectTrigger><SelectValue placeholder="Todos os tipos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  {TIPOS_MOVIMENTO.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.descricao}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">De</Label>
              <Input type="date" value={dtInicio} onChange={(e) => { setDtInicio(e.target.value); resetPagina(); }} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Até</Label>
              <Input type="date" value={dtFim} onChange={(e) => { setDtFim(e.target.value); resetPagina(); }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Movimentações
            {isFetching && <span className="text-xs font-normal text-muted-foreground">(atualizando...)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Saldo Anterior</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Saldo Atual</TableHead>
                  <TableHead>Justificativa</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : itens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      Nenhuma movimentação encontrada para os filtros selecionados
                    </TableCell>
                  </TableRow>
                ) : (
                  itens.map((item) => (
                    <TableRow key={`${item.movimentoId}-${item.subProdutoId}-${item.localEstoqueId}`}>
                      <TableCell className="font-medium max-w-[200px] truncate" title={item.produtoDescricao}>
                        {item.produtoDescricao}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.localDescricao}</TableCell>
                      <TableCell>
                        <Badge variant={tipoVariant(item.tipoMovimentoId)}>
                          {item.tipoMovimentoDesc}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtQtd(item.qtdSaldoAnterior)}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-medium ${item.tipoMovimentoId === 2 ? 'text-red-600' : 'text-green-600'}`}>
                        {item.tipoMovimentoId === 2 ? '−' : '+'}{fmtQtd(Math.abs(item.qtdMovimento))}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {fmtQtd(item.qtdSaldoAtual)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate" title={item.justificativa ?? ''}>
                        {item.justificativa ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{fmtDate(item.dtAtualizacao)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)          page = i + 1;
                  else if (pagina <= 3)                 page = i + 1;
                  else if (pagina >= totalPaginas - 2)  page = totalPaginas - 4 + i;
                  else                                  page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricoMovProduto;
